import { ChevronDown, ChevronUp, Code2, Info, Languages, Plus, Trash2 } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Tab } from '@headlessui/react';

import { Button } from '@/components/Common/button';
import { Card, CardContent, CardHeader } from '@/components/Common/Card';

interface SynonymEntry {
  id: string;
  term: string;
  synonyms: string[];
}

interface AbbreviationEntry {
  id: string;
  abbreviation: string;
  fullForm: string;
}

interface ThesaurusData {
  synonyms?: Record<string, string[]>;
  abbreviations?: Record<string, string>;
}

interface ThesaurusFormProps {
  initialContent?: string;
  onSave: (jsonContent: string) => Promise<void>;
  onCancel: () => void;
}

export function ThesaurusForm({ initialContent, onSave, onCancel }: ThesaurusFormProps) {
  const { t } = useTranslation();
  const [synonymEntries, setSynonymEntries] = useState<SynonymEntry[]>([]);
  const [abbreviationEntries, setAbbreviationEntries] = useState<AbbreviationEntry[]>([]);
  const [expandedSynonyms, setExpandedSynonyms] = useState<Set<string>>(new Set());
  const [expandedAbbreviations, setExpandedAbbreviations] = useState<Set<string>>(new Set());
  const [selectedTab, setSelectedTab] = useState(0);
  const [saving, setSaving] = useState(false);

  // Parse initial JSON content
  useEffect(() => {
    if (initialContent) {
      try {
        const parsed = JSON.parse(initialContent) as ThesaurusData;

        if (parsed.synonyms) {
          const synArray: SynonymEntry[] = Object.entries(parsed.synonyms).map(([term, synonyms]) => ({
            id: `syn-${Math.random().toString(36).substr(2, 9)}`,
            term,
            synonyms: synonyms || [],
          }));
          setSynonymEntries(synArray);
          // Cards closed by default
          setExpandedSynonyms(new Set());
        }

        if (parsed.abbreviations) {
          const abbArray: AbbreviationEntry[] = Object.entries(parsed.abbreviations).map(([abbr, full]) => ({
            id: `abb-${Math.random().toString(36).substr(2, 9)}`,
            abbreviation: abbr,
            fullForm: full,
          }));
          setAbbreviationEntries(abbArray);
          // Cards closed by default
          setExpandedAbbreviations(new Set());
        }
      } catch (error) {
        console.error('Failed to parse JSON:', error);
      }
    }
  }, [initialContent]);

  const handleAddSynonym = () => {
    const newEntry: SynonymEntry = {
      id: `syn-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      term: '',
      synonyms: [],
    };
    // Add at the beginning
    setSynonymEntries([newEntry, ...synonymEntries]);
    setExpandedSynonyms(new Set([...expandedSynonyms, newEntry.id]));
  };

  const handleAddAbbreviation = () => {
    const newEntry: AbbreviationEntry = {
      id: `abb-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      abbreviation: '',
      fullForm: '',
    };
    // Add at the beginning
    setAbbreviationEntries([newEntry, ...abbreviationEntries]);
    setExpandedAbbreviations(new Set([...expandedAbbreviations, newEntry.id]));
  };

  const handleRemoveSynonym = (id: string) => {
    setSynonymEntries(synonymEntries.filter(e => e.id !== id));
    const newExpanded = new Set(expandedSynonyms);
    newExpanded.delete(id);
    setExpandedSynonyms(newExpanded);
  };

  const handleRemoveAbbreviation = (id: string) => {
    setAbbreviationEntries(abbreviationEntries.filter(e => e.id !== id));
    const newExpanded = new Set(expandedAbbreviations);
    newExpanded.delete(id);
    setExpandedAbbreviations(newExpanded);
  };

  const handleUpdateSynonym = (id: string, field: keyof SynonymEntry, value: unknown) => {
    const updated = synonymEntries.map(entry =>
      entry.id === id ? { ...entry, [field]: value } : entry
    );
    setSynonymEntries(updated);
  };

  const handleUpdateAbbreviation = (id: string, field: keyof AbbreviationEntry, value: unknown) => {
    const updated = abbreviationEntries.map(entry =>
      entry.id === id ? { ...entry, [field]: value } : entry
    );
    setAbbreviationEntries(updated);
  };

  const handleAddSynonymItem = (entryId: string) => {
    const updated = synonymEntries.map(entry => {
      if (entry.id === entryId) {
        return {
          ...entry,
          synonyms: [...entry.synonyms, ''],
        };
      }
      return entry;
    });
    setSynonymEntries(updated);
  };

  const handleUpdateSynonymItem = (entryId: string, itemIndex: number, value: string) => {
    const updated = synonymEntries.map(entry => {
      if (entry.id === entryId) {
        const newSynonyms = [...entry.synonyms];
        newSynonyms[itemIndex] = value;
        return { ...entry, synonyms: newSynonyms };
      }
      return entry;
    });
    setSynonymEntries(updated);
  };

  const handleRemoveSynonymItem = (entryId: string, itemIndex: number) => {
    const updated = synonymEntries.map(entry => {
      if (entry.id === entryId) {
        return {
          ...entry,
          synonyms: entry.synonyms.filter((_, idx) => idx !== itemIndex),
        };
      }
      return entry;
    });
    setSynonymEntries(updated);
  };

  const toggleSynonymExpanded = (id: string) => {
    const newExpanded = new Set(expandedSynonyms);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedSynonyms(newExpanded);
  };

  const toggleAbbreviationExpanded = (id: string) => {
    const newExpanded = new Set(expandedAbbreviations);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedAbbreviations(newExpanded);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const synonymsObj: Record<string, string[]> = {};
      synonymEntries.forEach(entry => {
        if (entry.term.trim()) {
          synonymsObj[entry.term] = entry.synonyms.filter(s => s.trim() !== '');
        }
      });

      const abbreviationsObj: Record<string, string> = {};
      abbreviationEntries.forEach(entry => {
        if (entry.abbreviation.trim()) {
          abbreviationsObj[entry.abbreviation] = entry.fullForm;
        }
      });

      const data = {
        synonyms: synonymsObj,
        abbreviations: abbreviationsObj,
      };

      const jsonContent = JSON.stringify(data, null, 2);

      await onSave(jsonContent);
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-h-[70vh] overflow-y-auto px-1">
      {/* Tabs for Synonyms and Abbreviations */}
      <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
        <Tab.List className="flex gap-2 rounded-xl bg-muted p-1 mb-4">
          <Tab
            className={({ selected }) =>
              `flex-1 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors flex items-center justify-center gap-2
              ${selected ? 'bg-background text-foreground shadow' : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'}`
            }
          >
            <Languages className="h-4 w-4" />
            {t('admin.configForms.thesaurus.synonymsTab', { total: synonymEntries.length })}
          </Tab>
          <Tab
            className={({ selected }) =>
              `flex-1 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors flex items-center justify-center gap-2
              ${selected ? 'bg-background text-foreground shadow' : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'}`
            }
          >
            <Code2 className="h-4 w-4" />
            {t('admin.configForms.thesaurus.abbreviationsTab', { total: abbreviationEntries.length })}
          </Tab>
        </Tab.List>

        <Tab.Panels>
          {/* Synonyms Panel */}
          <Tab.Panel>
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-foreground">{t('admin.configForms.shared.synonyms')}</h3>
                <Button onClick={handleAddSynonym} size="sm">
                  <Plus className="h-4 w-4 mr-1" />
                  {t('admin.configForms.thesaurus.addTerm')}
                </Button>
              </div>

              <div className="space-y-3">
                {synonymEntries.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Info className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">{t('admin.configForms.thesaurus.noTerms')}</p>
                  </div>
                ) : (
                  synonymEntries.map((entry) => {
                    const isExpanded = expandedSynonyms.has(entry.id);

                    return (
                      <Card key={entry.id} className="border-border">
                        <CardHeader className="pb-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 flex-1">
                              <button
                                onClick={() => toggleSynonymExpanded(entry.id)}
                                className="p-1 hover:bg-muted rounded"
                              >
                                {isExpanded ? (
                                  <ChevronUp className="h-4 w-4" />
                                ) : (
                                  <ChevronDown className="h-4 w-4" />
                                )}
                              </button>
                              <input
                                type="text"
                                value={entry.term}
                                onChange={(e) => handleUpdateSynonym(entry.id, 'term', e.target.value)}
                                placeholder={t('admin.configForms.thesaurus.termPlaceholder')}
                                className="flex-1 px-3 py-1.5 text-sm font-semibold bg-background border border-border rounded focus:ring-2 focus:ring-primary focus:border-transparent"
                              />
                            </div>
                            <Button
                              onClick={() => handleRemoveSynonym(entry.id)}
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardHeader>

                        {isExpanded && (
                          <CardContent className="space-y-4 pt-0">
                            <div>
                              <label className="block text-xs font-medium text-foreground/70 mb-1">
                                {t('admin.configForms.thesaurus.synonymList')}
                              </label>
                              <div className="space-y-2">
                                {entry.synonyms.map((syn, synIndex) => (
                                  <div key={synIndex} className="flex gap-2">
                                    <input
                                      type="text"
                                      value={syn}
                                      onChange={(e) => handleUpdateSynonymItem(entry.id, synIndex, e.target.value)}
                                      placeholder={t('admin.configForms.shared.synonymPlaceholder')}
                                      className="flex-1 px-3 py-1.5 text-sm bg-background border border-border rounded focus:ring-2 focus:ring-primary focus:border-transparent"
                                    />
                                    <Button
                                      onClick={() => handleRemoveSynonymItem(entry.id, synIndex)}
                                      variant="ghost"
                                      size="sm"
                                      className="text-red-600"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                ))}
                                <Button
                                  onClick={() => handleAddSynonymItem(entry.id)}
                                  variant="outline"
                                  size="sm"
                                >
                                  <Plus className="h-3 w-3 mr-1" />
                                  {t('admin.configForms.shared.addSynonym')}
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        )}
                      </Card>
                    );
                  })
                )}
              </div>
            </div>
          </Tab.Panel>

          {/* Abbreviations Panel */}
          <Tab.Panel>
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-foreground">{t('admin.configForms.thesaurus.abbreviations')}</h3>
                <Button onClick={handleAddAbbreviation} size="sm">
                  <Plus className="h-4 w-4 mr-1" />
                  {t('admin.configForms.thesaurus.addAbbreviation')}
                </Button>
              </div>

              <div className="space-y-3">
                {abbreviationEntries.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Info className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">{t('admin.configForms.thesaurus.noAbbreviations')}</p>
                  </div>
                ) : (
                  abbreviationEntries.map((entry) => {
                    const isExpanded = expandedAbbreviations.has(entry.id);

                    return (
                      <Card key={entry.id} className="border-border">
                        <CardHeader className="pb-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 flex-1">
                              <button
                                onClick={() => toggleAbbreviationExpanded(entry.id)}
                                className="p-1 hover:bg-muted rounded"
                              >
                                {isExpanded ? (
                                  <ChevronUp className="h-4 w-4" />
                                ) : (
                                  <ChevronDown className="h-4 w-4" />
                                )}
                              </button>
                              <input
                                type="text"
                                value={entry.abbreviation}
                                onChange={(e) => handleUpdateAbbreviation(entry.id, 'abbreviation', e.target.value)}
                                placeholder={t('admin.configForms.thesaurus.abbreviationPlaceholder')}
                                className="w-48 px-3 py-1.5 text-sm font-semibold bg-background border border-border rounded focus:ring-2 focus:ring-primary focus:border-transparent"
                              />
                            </div>
                            <Button
                              onClick={() => handleRemoveAbbreviation(entry.id)}
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardHeader>

                        {isExpanded && (
                          <CardContent className="space-y-4 pt-0">
                            <div>
                              <label className="block text-xs font-medium text-foreground/70 mb-1">
                                {t('admin.configForms.thesaurus.fullForm')}
                              </label>
                              <input
                                type="text"
                                value={entry.fullForm}
                                onChange={(e) => handleUpdateAbbreviation(entry.id, 'fullForm', e.target.value)}
                                placeholder={t('admin.configForms.thesaurus.fullFormPlaceholder')}
                                className="w-full px-3 py-1.5 text-sm bg-background border border-border rounded focus:ring-2 focus:ring-primary focus:border-transparent"
                              />
                            </div>
                          </CardContent>
                        )}
                      </Card>
                    );
                  })
                )}
              </div>
            </div>
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>

      {/* Actions */}
      <div className="flex gap-3 justify-end pt-4 border-t border-border sticky bottom-0 bg-background">
        <Button onClick={onCancel} variant="outline" disabled={saving}>
          {t('actions.cancel')}
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? t('actions.saving') : t('actions.save')}
        </Button>
      </div>
    </div>
  );
}
